import React, { useState, useEffect, useMemo } from "react";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    DialogActions,
    Typography,
    OutlinedInput
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { ACTION_OPEN_ACCOUNT_MENU, ACTION_UPDATE_EMAIL } from "../../shared/constants";
import { isEmailValid } from "../../shared/shared";
import { Input } from "@progress/kendo-react-inputs";
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import SuccesDialog from "../success-dialog";
import {
    USER_CASEDESIGNER,
    USER_CONCIERGE,
    USER_FINANCIALPROFESSIONAL,
    USER_SUPERADMIN,
    ACTION_OPEN_PASSWORD_CHANGE_SUCCESS_MODAL
} from "../../shared/constants";
import { updateUserEmail, isEmailAddressDuplicated, changePassword } from "../../shared/api";
import { useCookies } from "react-cookie";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";


const PASSWORD_MODE_TEXT = "text";
const PASSWORD_MODE_PASSWORD = "password";

const AccountMenuModal = (props) => {
    const [emailEditable, setEmailEditable] = useState(false);
    const [emailAddress, setEmailAddress] = useState("");
    const [backupEmail, setBackupEmail] = useState("");
    const [showInvalidEmailDialog, setShowInvalidEmailDialog] = useState(false);
    const [showDuplicateEmailDialog, setShowDuplicateEmailDialog] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: ""
    });
    const [showPasswordNonMatchingModal, setShowPasswordNonMatchingModal] = useState(false);
    const [showInvalidPasswordModal, setShowInvalidPasswordModal] = useState(false);
    const [showPasswordNonConformingModal, setShowPasswordNonConformingModal] = useState(false);
    const [showEmptyPassword, setShowEmptyPassword] = useState(false);
    const [passwordMode, setPasswordMode] = useState({
        currentPasswordMode: PASSWORD_MODE_PASSWORD,
        newPasswordMode: PASSWORD_MODE_PASSWORD,
        confirmNewPasswordMode: PASSWORD_MODE_PASSWORD
    });

    const [cookies, setCookie] = useCookies(["loginToken"]);
    const dispatch = useDispatch();

    const loggedInUserRedux = useSelector((state) => state.dataStore.get("loginInfo"));
    const currentUserRedux = useSelector((state) => state.dataStore.get("currentUser"));

    const token = useSelector((state) => state.dataStore.getIn(["loginInfo", "authorizationToken"]));

    const authToken = useMemo(() => {
        if (token != null) {
            return token;
        }
        return cookies["loginToken"];
    });

    const loggedInUser = useMemo(() => {
        if (!loggedInUserRedux) return {};
        return {
            loginInfo: loggedInUserRedux.toJS(),
            currentUser: currentUserRedux.toJS()
        }
    }, [loggedInUserRedux, currentUserRedux]);

    const handleClose = () => {
        dispatch({
            type: ACTION_OPEN_ACCOUNT_MENU,
            value: false
        });
    };

    useEffect(() => {
        setEmailAddress(loggedInUser.loginInfo.userName);
        setBackupEmail(loggedInUser.loginInfo.userName);
    }, [loggedInUser]);

    const validatePasswordMatch = () => {
        if (passwordData.currentPassword === "" && passwordData.newPassword === "" && passwordData.confirmNewPassword === "") return true;
        return passwordData.newPassword === passwordData.confirmNewPassword && passwordData.currentPassword !== "";
    };

    const validateNewPasswordAcceptable = () => {
        if (passwordData.newPassword === "") return true;

        console.log(passwordData.newPassword);

        const re = /^([!@#$%^&*]*)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
        return re.test(passwordData.newPassword);
    };

    const isEmptyNewPassword = () => {
        if (passwordData.newPassword.length === 0 || passwordData.confirmNewPassword.length === 0) return false;
        return true;
    }

    const onSaveEmailClick = async () => {
        if (!isEmailValid(emailAddress)) {
            setShowInvalidEmailDialog(true);
            return;
        }

        if (emailAddress === backupEmail) {
            setEmailEditable(false);
            return;
        }

        const isDuplicated = await isEmailAddressDuplicated(emailAddress, authToken);
        if (isDuplicated) {
            setShowDuplicateEmailDialog(true);
            return;
        }

        await updateUserEmail(loggedInUser.loginInfo.userId, emailAddress, authToken);

        dispatch({
            type: ACTION_UPDATE_EMAIL,
            value: emailAddress
        });

        setBackupEmail(emailAddress);
        setEmailEditable(false);
    };

    const onSaveClick = async () => {
        if (!isEmptyNewPassword()) {
            setShowEmptyPassword(true);
            return;
        }

        if (!validatePasswordMatch()) {
            setShowPasswordNonMatchingModal(true);
            return;
        }

        if (!validateNewPasswordAcceptable()) {
            setShowPasswordNonConformingModal(true);
            return;
        }

        if (passwordData.currentPassword !== "") {
            const passwordResult = await changePassword(passwordData.currentPassword, passwordData.newPassword, authToken);
            if (!passwordResult) {
                setShowInvalidPasswordModal(true);
                return;
            }
            dispatch({
                type: ACTION_OPEN_PASSWORD_CHANGE_SUCCESS_MODAL
            });
            handleClose();
        }
    };

    const cancelEmailAddressChanges = () => {
        setEmailAddress(backupEmail);
        setEmailEditable(false);
    };

    const getRoleString = (role) => {
        switch (role.toUpperCase()) {
            case USER_SUPERADMIN:
                return "Super Admin";
            case USER_CONCIERGE:
                return "Concierge";
            case USER_FINANCIALPROFESSIONAL:
                return "Financial Professional";
            case USER_CASEDESIGNER:
                return "Case Designer";
        }
    };

    return (
        <Dialog
            fullWidth
            maxWidth={"sm"}
            open={true}
            onClose={() => {
                handleClose();
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description">

            <DialogTitle>
                <Grid className={"ifw-user-menu-header"} container spacing={3}>
                    <Grid item xs={10}>
                        <Typography component={"h1"} variant={"h1"}>
                            {loggedInUser.currentUser.name}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <CancelIcon onClick={handleClose} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography component={"label"} variant={"label"}>
                            {getRoleString(loggedInUser.currentUser.authorization)}
                        </Typography>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3}>
                    <Grid item xs={10}>
                        <Input value={emailAddress}
                            disabled={!emailEditable}
                            valid={() => {
                                return isEmailValid(emailAddress);
                            }}
                            label={"Email Address"}
                            onChange={(e) => {
                                setEmailAddress(e.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        {!emailEditable && <EditIcon onClick={() => {
                            setEmailEditable(true);
                        }} />}
                        {emailEditable && <React.Fragment>
                            <CheckIcon onClick={onSaveEmailClick} />
                            <CancelIcon onClick={cancelEmailAddressChanges} />
                        </React.Fragment>}
                    </Grid>
                    <Grid item xs={12}>
                        <hr />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography>Current Password</Typography>
                        <OutlinedInput
                            style={{ width: '100%' }}
                            placeholder={'Current password'}
                            type={passwordMode.currentPasswordMode}
                            autoComplete={'new-password'}
                            onChange={(e) => {
                                setPasswordData({
                                    ...passwordData,
                                    currentPassword: e.target.value
                                });
                            }}
                            value={passwordData.currentPassword}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => {
                                            setPasswordMode({
                                                ...passwordMode,
                                                currentPasswordMode: passwordMode.currentPasswordMode === PASSWORD_MODE_PASSWORD
                                                    ? PASSWORD_MODE_TEXT
                                                    : PASSWORD_MODE_PASSWORD
                                            });
                                        }}
                                        onMouseDown={() => {
                                            setPasswordMode({
                                                ...passwordMode,
                                                currentPasswordMode: passwordMode.currentPasswordMode === PASSWORD_MODE_PASSWORD
                                                    ? PASSWORD_MODE_TEXT
                                                    : PASSWORD_MODE_PASSWORD
                                            });
                                        }}
                                        edge="end"
                                    >
                                        {passwordMode.currentPasswordMode === PASSWORD_MODE_TEXT ? (
                                            <VisibilityIcon />
                                        ) : (
                                            <VisibilityOffIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography>New Password</Typography>
                        <OutlinedInput
                            type={passwordMode.newPasswordMode}
                            autoComplete="off"
                            value={passwordData.newPassword}
                            placeholder={"New Password"}
                            style={{ width: '100%' }}
                            onChange={(e) => {
                                setPasswordData({
                                    ...passwordData,
                                    newPassword: e.target.value
                                });
                            }}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => {
                                            setPasswordMode({
                                                ...passwordMode,
                                                newPasswordMode: passwordMode.newPasswordMode === PASSWORD_MODE_PASSWORD
                                                    ? PASSWORD_MODE_TEXT
                                                    : PASSWORD_MODE_PASSWORD
                                            });
                                        }}
                                        onMouseDown={() => {
                                            setPasswordMode({
                                                ...passwordMode,
                                                newPasswordMode: passwordMode.newPasswordMode === PASSWORD_MODE_PASSWORD
                                                    ? PASSWORD_MODE_TEXT
                                                    : PASSWORD_MODE_PASSWORD
                                            });
                                        }}
                                        edge="end"
                                    >
                                        {passwordMode.newPasswordMode === PASSWORD_MODE_TEXT ? (
                                            <VisibilityIcon />
                                        ) : (
                                            <VisibilityOffIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography>Confirm New Password</Typography>
                        <OutlinedInput
                            type={passwordMode.confirmNewPasswordMode}
                            autoComplete="off"
                            value={passwordData.confirmNewPassword}
                            placeholder={"Confirm New Password"}
                            style={{ width: '100%' }}
                            onChange={(e) => {
                                setPasswordData({
                                    ...passwordData,
                                    confirmNewPassword: e.target.value
                                });
                            }}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => {
                                            setPasswordMode({
                                                ...passwordMode,
                                                confirmNewPasswordMode: passwordMode.confirmNewPasswordMode === PASSWORD_MODE_PASSWORD
                                                    ? PASSWORD_MODE_TEXT
                                                    : PASSWORD_MODE_PASSWORD
                                            });
                                        }}
                                        onMouseDown={() => {
                                            setPasswordMode({
                                                ...passwordMode,
                                                confirmNewPasswordMode: passwordMode.confirmNewPasswordMode === PASSWORD_MODE_PASSWORD
                                                    ? PASSWORD_MODE_TEXT
                                                    : PASSWORD_MODE_PASSWORD
                                            });
                                        }}
                                        edge="end"
                                    >
                                        {passwordMode.confirmNewPasswordMode === PASSWORD_MODE_TEXT ? (
                                            <VisibilityIcon />
                                        ) : (
                                            <VisibilityOffIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </Grid>
                    {showInvalidEmailDialog && <SuccesDialog
                        open={true}
                        handleOk={() => {
                            setShowInvalidEmailDialog(false);
                        }}
                        message={"Please enter a valid email!"}
                    />}
                    {showDuplicateEmailDialog && <SuccesDialog
                        open={true}
                        handleOk={() => {
                            setShowDuplicateEmailDialog(false);
                        }}
                        message={"Email is already in use. Please enter a different email."}
                    />}
                    {showPasswordNonMatchingModal && <SuccesDialog
                        open={true}
                        handleOk={() => {
                            setShowPasswordNonMatchingModal(false);
                        }}
                        message={"Please provide your current password and ensure that your new password and confirmed password match!"}
                    />}
                    {showPasswordNonConformingModal && <SuccesDialog
                        open={true}
                        handleOk={() => {
                            setShowPasswordNonConformingModal(false);
                        }}
                        message={"Your password should be at least 8 characters long, have at least one uppercase and lowercase character."}
                    />}
                    {showInvalidPasswordModal && <SuccesDialog
                        open={true}
                        handleOk={() => {
                            setShowInvalidPasswordModal(false);
                        }}
                        message={"Please enter the correct current password."}
                    />}
                    {showEmptyPassword && <SuccesDialog
                        open={true}
                        handleOk={() => {
                            setShowEmptyPassword(false);
                        }}
                        message={"The new password or confirm new password cannot be blank."}
                    />
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={onSaveClick}>Change Password</Button>
            </DialogActions>
        </Dialog>)
};

export default AccountMenuModal;
